import { useEffect, useMemo, useState } from 'react'
import { RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import type {
  EmployeeManualOrder,
  ManualOrderApplyRefundParams,
  ManualOrderRefundEligibility,
  ManualOrderRefundEligibilityItem,
} from '@/api/employee-manual-order'

interface ManualOrderRefundDialogProps {
  order: EmployeeManualOrder | null
  eligibility?: ManualOrderRefundEligibility
  open: boolean
  isSubmitting?: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: ManualOrderApplyRefundParams) => void
}

type SelectedQuantities = Record<string, number>

const EMPTY_ITEMS: ManualOrderRefundEligibilityItem[] = []
const toCents = (value: number) => Math.round((Number.isFinite(value) ? value : 0) * 100)
const fromCents = (value: number) => Number((value / 100).toFixed(2))
const formatMoney = (value: number) => fromCents(toCents(value)).toFixed(2)
const parseAmountText = (value: string) => {
  const amount = Number.parseFloat(value)
  return Number.isFinite(amount) ? amount : null
}

const isItemSelectable = (item: ManualOrderRefundEligibilityItem) =>
  item.refundable_num > 0 && item.is_support_refund === 1

const getUnavailableReason = (item: ManualOrderRefundEligibilityItem) => {
  if (item.is_support_refund !== 1) return '该商品不支持退货'
  if (item.refundable_num <= 0) return '暂无可退数量'
  return ''
}

export function ManualOrderRefundDialog({
  order,
  eligibility,
  open,
  isSubmitting = false,
  onOpenChange,
  onSubmit,
}: ManualOrderRefundDialogProps) {
  const [selectedQuantities, setSelectedQuantities] = useState<SelectedQuantities>({})
  const [refundReason, setRefundReason] = useState('')
  const [refundExplain, setRefundExplain] = useState('')
  const [refundAmountText, setRefundAmountText] = useState('0.00')
  const items = eligibility?.items ?? EMPTY_ITEMS

  useEffect(() => {
    if (!open) return

    const initialQuantities = Object.fromEntries(items.map((item) => [item.cart_id, 0]))
    setSelectedQuantities(initialQuantities)
    setRefundReason('')
    setRefundExplain('')
    setRefundAmountText('0.00')
  }, [items, open])

  const selectedItems = useMemo(
    () =>
      items
        .filter((item) => isItemSelectable(item))
        .map((item) => ({
          item,
          quantity: selectedQuantities[item.cart_id] ?? 0,
        }))
        .filter(({ quantity }) => quantity > 0),
    [items, selectedQuantities],
  )

  const selectedMaxCents = useMemo(
    () =>
      selectedItems.reduce((total, { item, quantity }) => {
        const itemMaxCents = toCents(item.max_refund_price)
        const quantityMaxCents = toCents(item.unit_refund_price * quantity)
        return total + Math.min(itemMaxCents, quantityMaxCents)
      }, 0),
    [selectedItems],
  )

  const refundAmountCents = selectedMaxCents
  const parsedRefundAmount = parseAmountText(refundAmountText)
  const editedRefundAmountCents = parsedRefundAmount === null ? null : toCents(parsedRefundAmount)
  const selectedMaxAmount = fromCents(selectedMaxCents)
  const isSubmitDisabled =
    !order ||
    isSubmitting ||
    selectedItems.length === 0 ||
    refundReason.trim().length === 0 ||
    editedRefundAmountCents === null ||
    editedRefundAmountCents <= 0 ||
    editedRefundAmountCents > selectedMaxCents

  useEffect(() => {
    setRefundAmountText(fromCents(refundAmountCents).toFixed(2))
  }, [refundAmountCents])

  const handleToggleItem = (item: ManualOrderRefundEligibilityItem, checked: boolean) => {
    if (!isItemSelectable(item)) return

    setSelectedQuantities((current) => ({
      ...current,
      // 勾选即按可退上限全额退款，可手动调小
      [item.cart_id]: checked ? item.refundable_num : 0,
    }))
  }

  // 全部退款：勾选所有可退商品并按可退上限全额退款
  const selectableItems = useMemo(() => items.filter(isItemSelectable), [items])
  const isAllSelected =
    selectableItems.length > 0 &&
    selectableItems.every((item) => (selectedQuantities[item.cart_id] ?? 0) > 0)

  // 是否所有可退商品都按可退上限勾选
  const isAllQuantitiesSelected =
    selectableItems.length > 0 &&
    selectableItems.every((item) => (selectedQuantities[item.cart_id] ?? 0) === item.refundable_num)
  // 已选商品但未全选数量 → 部分退款
  const isPartialRefund = selectedItems.length > 0 && !isAllQuantitiesSelected
  // 退款金额低于所选商品的可退上限 → 金额不匹配
  const isAmountMismatch =
    selectedItems.length > 0 &&
    editedRefundAmountCents !== null &&
    editedRefundAmountCents > 0 &&
    editedRefundAmountCents < selectedMaxCents
  // 全额退款：全部商品按上限勾选且金额等于所选上限
  const isFullRefund =
    isAllQuantitiesSelected &&
    editedRefundAmountCents !== null &&
    editedRefundAmountCents === selectedMaxCents

  const handleToggleAll = () => {
    setSelectedQuantities((current) => {
      const next = { ...current }
      for (const item of selectableItems) {
        next[item.cart_id] = isAllSelected ? 0 : item.refundable_num
      }
      return next
    })
  }

  const handleQuantityChange = (item: ManualOrderRefundEligibilityItem, value: string) => {
    const nextValue = Number.parseInt(value, 10)
    const quantity = Number.isNaN(nextValue)
      ? 1
      : Math.min(Math.max(nextValue, 1), item.refundable_num)

    setSelectedQuantities((current) => ({
      ...current,
      [item.cart_id]: quantity,
    }))
  }

  const handleSubmit = () => {
    if (isSubmitDisabled) return

    onSubmit({
      refund_reason: refundReason.trim(),
      refund_explain: refundExplain.trim(),
      refund_price: fromCents(editedRefundAmountCents ?? 0),
      cart_ids: selectedItems.map(({ item, quantity }) => ({
        cart_id: item.cart_id,
        cart_num: quantity,
      })),
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[680px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="h-4 w-4" />
            申请退货
          </DialogTitle>
          <DialogDescription>
            {order ? `订单号：${order.order_id}` : '选择需要退货的商品并填写申请原因'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {eligibility && !eligibility.can_apply && eligibility.reason && (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {eligibility.reason}
            </div>
          )}

          {isPartialRefund && (
            <div
              data-testid="partial-refund-hint"
              className="rounded-md border border-amber-500/50 bg-amber-50 px-3 py-2 text-sm text-amber-700"
            >
              注意：本次为<strong>部分退款</strong>，未勾选全部可退商品或数量。
              退款完成后订单将被拆分为两个订单：已退款部分和剩余部分。
            </div>
          )}
          {isAmountMismatch && (
            <div
              data-testid="amount-mismatch-hint"
              className="rounded-md border border-amber-500/50 bg-amber-50 px-3 py-2 text-sm text-amber-700"
            >
              注意：退款金额 ¥{fromCents(editedRefundAmountCents ?? 0).toFixed(2)}
              低于所选商品可退上限 ¥{selectedMaxAmount.toFixed(2)}，请确认金额是否正确。
            </div>
          )}
          {isFullRefund && (
            <div
              data-testid="full-refund-hint"
              className="rounded-md border border-emerald-500/40 bg-emerald-50 px-3 py-2 text-sm text-emerald-700"
            >
              已选择全部可退商品并按全额退款。
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="refund-select-all"
                  aria-label="全部退款"
                  checked={isAllSelected}
                  disabled={selectableItems.length === 0 || isSubmitting}
                  onCheckedChange={() => handleToggleAll()}
                />
                <label htmlFor="refund-select-all" className="cursor-pointer font-medium">
                  全部退款
                </label>
                <span className="font-medium">退货商品</span>
              </div>
              <span className="text-muted-foreground">
                可退上限 ¥{formatMoney(eligibility?.max_refund_price ?? 0)}
              </span>
            </div>

            <div className="divide-y rounded-md border">
              {items.length > 0 ? (
                items.map((item) => {
                  const selectable = isItemSelectable(item)
                  const quantity = selectedQuantities[item.cart_id] ?? 0
                  const checked = quantity > 0
                  const itemAmount = fromCents(toCents(item.unit_refund_price * quantity))
                  const checkboxId = `refund-item-${item.cart_id}`

                  return (
                    <div
                      key={item.cart_id}
                      data-testid={`refund-item-${item.cart_id}`}
                      className="grid gap-3 p-3 sm:grid-cols-[1fr_96px_92px]"
                    >
                      <div className="flex min-w-0 items-start gap-3">
                        <Checkbox
                          id={checkboxId}
                          aria-label={item.product_name}
                          checked={checked}
                          disabled={!selectable || isSubmitting}
                          onCheckedChange={(checkedValue) => handleToggleItem(item, checkedValue === true)}
                        />
                        {item.product_image ? (
                          <img
                            src={item.product_image}
                            alt={item.product_name}
                            className="h-12 w-12 rounded border object-cover"
                          />
                        ) : (
                          <div className="h-12 w-12 rounded border bg-muted" />
                        )}
                        <label htmlFor={checkboxId} className="min-w-0 flex-1 text-sm">
                          <span className="block truncate font-medium">{item.product_name}</span>
                          {item.product_attr && (
                            <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                              {item.product_attr}
                            </span>
                          )}
                          <span className="mt-1 block text-xs text-muted-foreground">
                            已购 {item.cart_num}，已退 {item.refunded_num}，可退 {item.refundable_num}
                          </span>
                          {!selectable && (
                            <span className="mt-1 block text-xs text-destructive">
                              {getUnavailableReason(item)}
                            </span>
                          )}
                        </label>
                      </div>

                      <div className="space-y-1">
                        <label className="text-xs text-muted-foreground" htmlFor={`refund-quantity-${item.cart_id}`}>
                          退款数量
                        </label>
                        <Input
                          id={`refund-quantity-${item.cart_id}`}
                          aria-label={`${item.product_name}退款数量`}
                          type="number"
                          min={1}
                          max={item.refundable_num}
                          value={checked ? quantity : ''}
                          disabled={!checked || !selectable || isSubmitting}
                          onChange={(event) => handleQuantityChange(item, event.target.value)}
                        />
                      </div>

                      <div className="space-y-1 text-left sm:text-right">
                        <div className="text-xs text-muted-foreground">退款金额</div>
                        <div className="text-sm font-medium">¥{itemAmount.toFixed(2)}</div>
                        <div className="text-xs text-muted-foreground">
                          单件 ¥{formatMoney(item.unit_refund_price)}
                        </div>
                      </div>
                    </div>
                  )
                })
              ) : (
                <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                  暂无可退商品信息
                </div>
              )}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-sm font-medium" htmlFor="manual-order-refund-amount">
                退款金额
              </label>
              <Input
                id="manual-order-refund-amount"
                aria-label="退款金额"
                type="number"
                min={0}
                max={selectedMaxAmount}
                step="0.01"
                value={refundAmountText}
                disabled={isSubmitting}
                onChange={(event) => setRefundAmountText(event.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                按所选商品自动计算，当前上限 ¥{selectedMaxAmount.toFixed(2)}
              </p>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium" htmlFor="manual-order-refund-reason">
                退款原因
              </label>
              <Input
                id="manual-order-refund-reason"
                aria-label="退款原因"
                value={refundReason}
                disabled={isSubmitting}
                maxLength={80}
                placeholder="请输入退款原因"
                onChange={(event) => setRefundReason(event.target.value)}
              />
              <p className="text-xs text-muted-foreground">必填</p>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium" htmlFor="manual-order-refund-explain">
              退款说明
            </label>
            <Textarea
              id="manual-order-refund-explain"
              aria-label="退款说明"
              value={refundExplain}
              disabled={isSubmitting}
              rows={3}
              placeholder="可补充退货原因、沟通记录等"
              onChange={(event) => setRefundExplain(event.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            取消
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitDisabled}>
            {isSubmitting ? '提交中...' : '提交申请'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
